import { useState, useEffect, useRef, useCallback } from "react";
import {
    Dialog,
    Box,
    Button,
    Typography,
    TextField,
    IconButton,
    Paper,
    CircularProgress
} from "@mui/material";
import {
    Close as CloseIcon,
    ArrowForward as BackIcon,
    Save as SaveIcon,
    Visibility as PreviewIcon,
    CloudUpload as UploadIcon,
    Add as AddIcon
} from "@mui/icons-material";
import QuestionTypeSidebar from "./QuestionTypeSidebar";
import QuestionCard from "./QuestionCard";
import FormPreviewDialog from "./FormPreviewDialog";
import { getDefaultQuestionData, duplicateQuestion } from "../../enums/QuestionTypes";
import { generateId, getCurrentDate } from "../../utils/formUtils";
import { uploadImage } from "../../services/MediaUploadService";

const FormBuilderDialog = ({ open, onClose, onSave, editingForm }) => {
    const [formTitle, setFormTitle] = useState("טופס חדש");
    const [logoUrl, setLogoUrl] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [showPreview, setShowPreview] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]); // Array of question IDs with errors

    // Store initial state for comparison (to detect changes in edit mode)
    const initialStateRef = useRef(null);

    // Refs for scrolling to error questions
    const questionRefs = useRef({});

    // Drag and drop state
    const [dragState, setDragState] = useState({
        isDragging: false,
        dragType: null, // 'new' or 'reorder'
        draggedIndex: null,
        dropTargetIndex: null,
        draggedType: null // for new components
    });

    const dragCounterRef = useRef(0);
    const formContentRef = useRef(null);
    const autoScrollIntervalRef = useRef(null);

    // Load form data when editing
    useEffect(() => {
        if (editingForm) {
            setFormTitle(editingForm.title || "טופס חדש");
            setLogoUrl(editingForm.logoUrl || null);
            setQuestions(editingForm.questions || []);
            // Store initial state for comparison
            initialStateRef.current = {
                title: editingForm.title || "טופס חדש",
                logoUrl: editingForm.logoUrl || null,
                questions: JSON.stringify(editingForm.questions || [])
            };
        } else {
            // Reset for new form
            setFormTitle("טופס חדש");
            setLogoUrl(null);
            setQuestions([]);
            initialStateRef.current = null;
        }
    }, [editingForm, open]);

    // Reset drag state when dialog closes
    useEffect(() => {
        if (!open) {
            setDragState({
                isDragging: false,
                dragType: null,
                draggedIndex: null,
                dropTargetIndex: null,
                draggedType: null
            });
            dragCounterRef.current = 0;
            if (autoScrollIntervalRef.current) {
                clearInterval(autoScrollIntervalRef.current);
                autoScrollIntervalRef.current = null;
            }
        }
    }, [open]);

    // Auto-scroll during drag when near edges
    useEffect(() => {
        if (!dragState.isDragging) {
            if (autoScrollIntervalRef.current) {
                clearInterval(autoScrollIntervalRef.current);
                autoScrollIntervalRef.current = null;
            }
            return;
        }

        const handleDragOver = (e) => {
            if (!formContentRef.current || !dragState.isDragging) return;

            const container = formContentRef.current;
            const rect = container.getBoundingClientRect();
            const scrollThreshold = 80; // pixels from edge to start scrolling
            const scrollSpeed = 15; // pixels per interval

            // Clear existing interval
            if (autoScrollIntervalRef.current) {
                clearInterval(autoScrollIntervalRef.current);
                autoScrollIntervalRef.current = null;
            }

            const mouseY = e.clientY;
            const topEdge = rect.top + scrollThreshold;
            const bottomEdge = rect.bottom - scrollThreshold;

            if (mouseY < topEdge && container.scrollTop > 0) {
                // Scroll up
                autoScrollIntervalRef.current = setInterval(() => {
                    container.scrollTop -= scrollSpeed;
                }, 16);
            } else if (mouseY > bottomEdge && container.scrollTop < container.scrollHeight - container.clientHeight) {
                // Scroll down
                autoScrollIntervalRef.current = setInterval(() => {
                    container.scrollTop += scrollSpeed;
                }, 16);
            }
        };

        document.addEventListener('dragover', handleDragOver);

        return () => {
            document.removeEventListener('dragover', handleDragOver);
            if (autoScrollIntervalRef.current) {
                clearInterval(autoScrollIntervalRef.current);
                autoScrollIntervalRef.current = null;
            }
        };
    }, [dragState.isDragging]);

    const [isUploadingLogo, setIsUploadingLogo] = useState(false);

    const handleLogoUpload = async (event) => {
        const file = event.target.files?.[0];
        if (file) {
            setIsUploadingLogo(true);
            try {
                // Upload to CDN and get URL
                const cdnUrl = await uploadImage(file);
                setLogoUrl(cdnUrl);
            } catch (error) {
                console.error('Error uploading logo:', error);
                alert('שגיאה בהעלאת הלוגו');
            } finally {
                setIsUploadingLogo(false);
            }
        }
    };

    const handleRemoveLogo = () => {
        setLogoUrl(null);
    };

    const handleAddQuestion = useCallback((type, insertAtIndex = null) => {
        const newQuestion = getDefaultQuestionData(type);
        setQuestions(prev => {
            if (insertAtIndex !== null && insertAtIndex >= 0) {
                const newQuestions = [...prev];
                newQuestions.splice(insertAtIndex, 0, newQuestion);
                return newQuestions;
            }
            return [...prev, newQuestion];
        });
    }, []);

    const handleUpdateQuestion = useCallback((id, updates) => {
        setQuestions(prev => prev.map(q =>
            q.id === id ? { ...q, ...updates } : q
        ));
    }, []);

    const handleDeleteQuestion = useCallback((id) => {
        setQuestions(prev => prev.filter(q => q.id !== id));
    }, []);

    const handleDuplicateQuestion = useCallback((id) => {
        setQuestions(prev => {
            const questionToDuplicate = prev.find(q => q.id === id);
            if (questionToDuplicate) {
                const newQuestion = duplicateQuestion(questionToDuplicate);
                const index = prev.findIndex(q => q.id === id);
                const newQuestions = [...prev];
                newQuestions.splice(index + 1, 0, newQuestion);
                return newQuestions;
            }
            return prev;
        });
    }, []);

    // Card drag handlers (for reordering)
    const handleCardDragStart = useCallback((e, index) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("cardIndex", index.toString());

        setDragState({
            isDragging: true,
            dragType: 'reorder',
            draggedIndex: index,
            dropTargetIndex: null,
            draggedType: null
        });
    }, []);

    const handleCardDragEnd = useCallback(() => {
        setDragState({
            isDragging: false,
            dragType: null,
            draggedIndex: null,
            dropTargetIndex: null,
            draggedType: null
        });
        dragCounterRef.current = 0;
    }, []);

    // Sidebar drag handlers
    const handleSidebarDragStart = useCallback((typeKey) => {
        setDragState({
            isDragging: true,
            dragType: 'new',
            draggedIndex: null,
            dropTargetIndex: null,
            draggedType: typeKey
        });
    }, []);

    const handleSidebarDragEnd = useCallback(() => {
        setDragState({
            isDragging: false,
            dragType: null,
            draggedIndex: null,
            dropTargetIndex: null,
            draggedType: null
        });
        dragCounterRef.current = 0;
    }, []);

    // Drop zone handlers
    const handleDragEnterZone = useCallback((e, index) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current++;

        setDragState(prev => ({
            ...prev,
            dropTargetIndex: index
        }));
    }, []);

    const handleDragLeaveZone = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current--;

        if (dragCounterRef.current === 0) {
            setDragState(prev => ({
                ...prev,
                dropTargetIndex: null
            }));
        }
    }, []);

    const handleDragOverZone = useCallback((e, index) => {
        e.preventDefault();
        e.stopPropagation();

        // Set drop effect based on drag type
        if (dragState.dragType === 'new') {
            e.dataTransfer.dropEffect = "copy";
        } else {
            e.dataTransfer.dropEffect = "move";
        }

        // Update target if changed
        if (dragState.dropTargetIndex !== index) {
            setDragState(prev => ({
                ...prev,
                dropTargetIndex: index
            }));
        }
    }, [dragState.dragType, dragState.dropTargetIndex]);

    const handleDropOnZone = useCallback((e, targetIndex) => {
        e.preventDefault();
        e.stopPropagation();

        const { dragType, draggedIndex, draggedType } = dragState;

        if (dragType === 'new' && draggedType) {
            // Adding new component from sidebar
            handleAddQuestion(draggedType, targetIndex);
        } else if (dragType === 'reorder' && draggedIndex !== null) {
            // Reordering existing question
            const fromIndex = draggedIndex;

            // Don't do anything if dropping in same position or adjacent position
            if (fromIndex !== targetIndex && fromIndex !== targetIndex - 1) {
                setQuestions(prev => {
                    const newQuestions = [...prev];
                    const [movedItem] = newQuestions.splice(fromIndex, 1);
                    const adjustedTarget = fromIndex < targetIndex ? targetIndex - 1 : targetIndex;
                    newQuestions.splice(adjustedTarget, 0, movedItem);
                    return newQuestions;
                });
            }
        } else {
            // Fallback: check dataTransfer for new component
            const questionType = e.dataTransfer.getData("questionType");
            if (questionType) {
                handleAddQuestion(questionType, targetIndex);
            }
        }

        // Reset state
        setDragState({
            isDragging: false,
            dragType: null,
            draggedIndex: null,
            dropTargetIndex: null,
            draggedType: null
        });
        dragCounterRef.current = 0;
    }, [dragState, handleAddQuestion]);

    // Validate questions and return array of question IDs with errors
    const validateQuestions = useCallback(() => {
        const errors = [];
        questions.forEach((q) => {
            switch (q.type) {
                case 'link':
                    if (!q.linkUrl || q.linkUrl.trim() === '') {
                        errors.push(q.id);
                    }
                    break;
                case 'pdf-attachment':
                    if (!q.pdfUrl || q.pdfUrl.trim() === '') {
                        errors.push(q.id);
                    }
                    break;
                case 'multiple-choice':
                    if (!q.options || q.options.length < 2) {
                        errors.push(q.id);
                    }
                    break;
                default:
                    break;
            }
        });
        return errors;
    }, [questions]);

    // Scroll to first error question
    const scrollToFirstError = useCallback((errorIds) => {
        if (errorIds.length > 0) {
            const firstErrorId = errorIds[0];
            const ref = questionRefs.current[firstErrorId];
            if (ref) {
                ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, []);

    // Check if form has changes compared to initial state
    const hasChanges = useCallback(() => {
        if (!editingForm || !initialStateRef.current) {
            return true; // New form - always allow save
        }
        const initial = initialStateRef.current;
        return (
            formTitle !== initial.title ||
            logoUrl !== initial.logoUrl ||
            JSON.stringify(questions) !== initial.questions
        );
    }, [editingForm, formTitle, logoUrl, questions]);

    const handleSave = () => {
        // Check if there are changes in edit mode
        if (editingForm && !hasChanges()) {
            onClose(); // Just close without saving if no changes
            return;
        }

        // Validate questions
        const errors = validateQuestions();
        setValidationErrors(errors);

        if (errors.length > 0) {
            // Scroll to first error after state update
            setTimeout(() => scrollToFirstError(errors), 100);
            return; // Don't save if there are errors
        }

        const now = getCurrentDate();
        const formData = {
            id: editingForm?.id || `form-${generateId()}`,
            title: formTitle,
            logoUrl,
            questions,
            createdAt: editingForm?.createdAt || now,
            lastUpdated: now,
            recipients: editingForm?.recipients || []
        };
        onSave(formData);
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullScreen
                PaperProps={{
                    sx: {
                        backgroundColor: "#F8FAFC"
                    }
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        backgroundColor: "#FFFFFF",
                        borderBottom: "1px solid #E2E8F0",
                        padding: "12px 24px",
                        display: "flex",
                        alignItems: "center",
                        gap: "16px",
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                        direction: "rtl"
                    }}
                >
                    <IconButton onClick={onClose} sx={{ color: "#64748B" }}>
                        <BackIcon />
                    </IconButton>
                    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, maxWidth: "400px" }}>
                        <TextField
                            value={formTitle}
                            onChange={(e) => setFormTitle(e.target.value)}
                            variant="standard"
                            placeholder="שם הטופס"
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    fontSize: "20px",
                                    fontWeight: 600,
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }
                            }}
                        />
                        {editingForm && (
                            <Typography sx={{
                                fontSize: "12px",
                                color: "#F59E0B",
                                fontFamily: '"Heebo", "Roboto", sans-serif',
                                marginTop: "4px"
                            }}>
                                עריכה תיצור עותק חדש של הטופס
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Button
                        variant="outlined"
                        startIcon={<PreviewIcon sx={{ marginLeft: "10px" }}/>}
                        onClick={() => setShowPreview(true)}
                        sx={{
                            borderColor: "#E2E8F0",
                            color: "#64748B",
                            fontFamily: '"Heebo", "Roboto", sans-serif',
                            "&:hover": {
                                borderColor: "#CBD5E1",
                                backgroundColor: "#F8FAFC"
                            }
                        }}
                    >
                        תצוגה מקדימה
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon sx={{ marginLeft: "10px" }} />}
                        onClick={handleSave}
                        sx={{
                            backgroundColor: "#57837E",
                            fontFamily: '"Heebo", "Roboto", sans-serif',
                            fontWeight: 600,
                            "&:hover": {
                                backgroundColor: "#46706B"
                            }
                        }}
                    >
                        שמור טופס
                    </Button>
                </Box>

                {/* Main Content - Flex container with independent scrolling */}
                <Box
                    sx={{
                        display: "flex",
                        flex: 1,
                        overflow: "hidden",
                        direction: "rtl",
                        gap: "24px",
                        padding: "24px"
                    }}
                >
                    {/* Sidebar - Independent scroll (hidden scrollbar) */}
                    <Box
                        sx={{
                            width: "280px",
                            minWidth: "280px",
                            height: "100%",
                            overflowY: "auto",
                            overflowX: "hidden",
                            // Hide scrollbar but keep functionality
                            scrollbarWidth: "none", // Firefox
                            msOverflowStyle: "none", // IE/Edge
                            "&::-webkit-scrollbar": {
                                display: "none" // Chrome/Safari
                            }
                        }}
                    >
                        <QuestionTypeSidebar
                            onDragStart={handleSidebarDragStart}
                            onDragEnd={handleSidebarDragEnd}
                            onAddQuestion={(type) => handleAddQuestion(type, null)}
                        />
                    </Box>

                    {/* Form Content - Independent scroll */}
                    <Box
                        ref={formContentRef}
                        sx={{
                            flex: 1,
                            height: "100%",
                            overflowY: "auto",
                            overflowX: "hidden",
                            paddingLeft: "8px",
                            "&::-webkit-scrollbar": {
                                width: "8px"
                            },
                            "&::-webkit-scrollbar-track": {
                                backgroundColor: "#F1F5F9",
                                borderRadius: "4px"
                            },
                            "&::-webkit-scrollbar-thumb": {
                                backgroundColor: "#CBD5E1",
                                borderRadius: "4px",
                                "&:hover": {
                                    backgroundColor: "#94A3B8"
                                }
                            }
                        }}
                    >
                        {/* Logo Section */}
                        <Paper
                            elevation={0}
                            sx={{
                                backgroundColor: "#FFFFFF",
                                border: "1px solid #E2E8F0",
                                borderRadius: "12px",
                                padding: "20px",
                                marginBottom: "16px"
                            }}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    fontSize: "14px",
                                    color: "#1E293B",
                                    marginBottom: "12px",
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }}
                            >
                                לוגו הטופס
                            </Typography>
                            {isUploadingLogo ? (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "12px",
                                        border: "2px dashed #57837E",
                                        borderRadius: "8px",
                                        padding: "32px",
                                        backgroundColor: "#F8FAFC"
                                    }}
                                >
                                    <CircularProgress size={24} sx={{ color: "#57837E" }} />
                                    <Typography sx={{ color: "#57837E", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                        מעלה לוגו...
                                    </Typography>
                                </Box>
                            ) : logoUrl ? (
                                <Box sx={{ position: "relative", display: "inline-block" }}>
                                    <img
                                        src={logoUrl}
                                        alt="Form Logo"
                                        style={{
                                            height: "80px",
                                            objectFit: "contain",
                                            borderRadius: "8px",
                                            border: "1px solid #E2E8F0"
                                        }}
                                    />
                                    <IconButton
                                        onClick={handleRemoveLogo}
                                        size="small"
                                        sx={{
                                            position: "absolute",
                                            top: -8,
                                            left: -8,
                                            backgroundColor: "#EF4444",
                                            color: "#FFFFFF",
                                            "&:hover": {
                                                backgroundColor: "#DC2626"
                                            }
                                        }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ) : (
                                <label>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: "8px",
                                            border: "2px dashed #E2E8F0",
                                            borderRadius: "8px",
                                            padding: "32px",
                                            cursor: "pointer",
                                            transition: "all 0.2s ease",
                                            "&:hover": {
                                                borderColor: "#57837E",
                                                backgroundColor: "#F8FAFC"
                                            }
                                        }}
                                    >
                                        <UploadIcon sx={{ color: "#64748B" }} />
                                        <Typography sx={{ color: "#64748B", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                            העלה לוגו
                                        </Typography>
                                    </Box>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleLogoUpload}
                                        style={{ display: "none" }}
                                    />
                                </label>
                            )}
                        </Paper>

                        {/* Questions List */}
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                minHeight: questions.length > 0 ? "200px" : "0px"
                            }}
                        >
                            {questions.map((question, index) => {
                                const { isDragging, dropTargetIndex, draggedIndex } = dragState;
                                const isActiveDropZone = dropTargetIndex === index;
                                const isDraggedItem = draggedIndex === index;

                                return (
                                    <Box
                                        key={question.id}
                                        sx={{
                                            opacity: isDraggedItem ? 0.4 : 1,
                                            transition: "opacity 0.15s ease"
                                        }}
                                    >
                                        {/* Drop zone before each item */}
                                        <Box
                                            onDrop={(e) => handleDropOnZone(e, index)}
                                            onDragOver={(e) => handleDragOverZone(e, index)}
                                            onDragEnter={(e) => handleDragEnterZone(e, index)}
                                            onDragLeave={handleDragLeaveZone}
                                            sx={{
                                                height: isDragging ? "60px" : "8px",
                                                borderRadius: "12px",
                                                transition: "all 0.2s ease-out",
                                                margin: isDragging ? "8px 0" : "4px 0",
                                                border: isActiveDropZone ? "2px dashed #57837E" : (isDragging ? "2px dashed #CBD5E1" : "2px dashed transparent"),
                                                backgroundColor: isActiveDropZone ? "rgba(87, 131, 126, 0.15)" : "transparent",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center"
                                            }}
                                        >
                                            {isActiveDropZone && (
                                                <Typography sx={{
                                                    fontSize: "14px",
                                                    color: "#57837E",
                                                    fontWeight: 600,
                                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                                }}>
                                                    שחרר כאן להוספה מעל
                                                </Typography>
                                            )}
                                        </Box>

                                        {/* Question Card */}
                                        <Box ref={(el) => questionRefs.current[question.id] = el}>
                                            <QuestionCard
                                                question={question}
                                                onUpdate={(updates) => {
                                                    handleUpdateQuestion(question.id, updates);
                                                    // Clear error for this question when user makes changes
                                                    if (validationErrors.includes(question.id)) {
                                                        setValidationErrors(prev => prev.filter(id => id !== question.id));
                                                    }
                                                }}
                                                onDelete={() => handleDeleteQuestion(question.id)}
                                                onDuplicate={() => handleDuplicateQuestion(question.id)}
                                                onDragStart={(e) => handleCardDragStart(e, index)}
                                                onDragEnd={handleCardDragEnd}
                                                hasError={validationErrors.includes(question.id)}
                                            />
                                        </Box>
                                    </Box>
                                );
                            })}

                            {/* Drop zone after last item */}
                            {questions.length > 0 && (
                                <Box
                                    onDrop={(e) => handleDropOnZone(e, questions.length)}
                                    onDragOver={(e) => handleDragOverZone(e, questions.length)}
                                    onDragEnter={(e) => handleDragEnterZone(e, questions.length)}
                                    onDragLeave={handleDragLeaveZone}
                                    sx={{
                                        height: dragState.isDragging ? "60px" : "8px",
                                        borderRadius: "12px",
                                        transition: "all 0.2s ease-out",
                                        margin: dragState.isDragging ? "8px 0" : "4px 0",
                                        border: dragState.dropTargetIndex === questions.length ? "2px dashed #57837E" : (dragState.isDragging ? "2px dashed #CBD5E1" : "2px dashed transparent"),
                                        backgroundColor: dragState.dropTargetIndex === questions.length ? "rgba(87, 131, 126, 0.15)" : "transparent",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center"
                                    }}
                                >
                                    {dragState.dropTargetIndex === questions.length && (
                                        <Typography sx={{
                                            fontSize: "14px",
                                            color: "#57837E",
                                            fontWeight: 600,
                                            fontFamily: '"Heebo", "Roboto", sans-serif'
                                        }}>
                                            שחרר כאן להוספה בסוף
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Box>

                        {/* Empty state */}
                        {questions.length === 0 && (
                            <Paper
                                elevation={0}
                                onDrop={(e) => handleDropOnZone(e, 0)}
                                onDragOver={(e) => handleDragOverZone(e, 0)}
                                onDragEnter={(e) => handleDragEnterZone(e, 0)}
                                onDragLeave={handleDragLeaveZone}
                                sx={{
                                    backgroundColor: "#FFFFFF",
                                    border: dragState.isDragging ? "2px dashed #57837E" : "1px solid #E2E8F0",
                                    borderRadius: "12px",
                                    padding: "48px 24px",
                                    textAlign: "center",
                                    transition: "all 0.2s ease",
                                    ...(dragState.isDragging && {
                                        backgroundColor: "rgba(87, 131, 126, 0.05)"
                                    })
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        borderRadius: "50%",
                                        backgroundColor: "#E8F5F3",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        margin: "0 auto 16px"
                                    }}
                                >
                                    <AddIcon sx={{ fontSize: 32, color: "#57837E" }} />
                                </Box>
                                <Typography
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: "18px",
                                        color: "#1E293B",
                                        marginBottom: "8px",
                                        fontFamily: '"Heebo", "Roboto", sans-serif'
                                    }}
                                >
                                    התחל לבנות את הטופס שלך
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: "14px",
                                        color: "#64748B",
                                        fontFamily: '"Heebo", "Roboto", sans-serif'
                                    }}
                                >
                                    גרור רכיב מהתפריט הימני כדי להתחיל
                                </Typography>
                            </Paper>
                        )}

                        {/* Bottom padding for scroll */}
                        <Box sx={{ height: "24px" }} />
                    </Box>
                </Box>
            </Dialog>

            <FormPreviewDialog
                open={showPreview}
                onClose={() => setShowPreview(false)}
                form={{
                    title: formTitle,
                    logoUrl,
                    questions
                }}
            />
        </>
    );
};

export default FormBuilderDialog;
